import { useLocalization, IconChevronRight, IconPencilLine, Modal, Tooltip, Button } from '@magicdoorinc/ui';
import { createMemo, Show, createSignal } from 'solid-js';
import { Portal } from 'solid-js/web';
import CloseIcon from '~/assets/images/quick-link/close-icon.svg?component-solid';
import LinkIcon from '~/assets/images/quick-link/link-icon.svg?component-solid';
import { useMenu } from '~/components/layouts/Menu/MenuContext';
import { FeedbackModal } from '~/components/layouts/Navigation/FeedbackModal';
import { useCurrentCompany } from '~/contexts/global';
import { useDeviceDetect } from '~/hooks/usedeviceDetect';
import { QuickLinksPanel } from './QuickLinksPanel';

export const LinkToWebsite = (props: { isMenuCollapsed?: boolean; tooltipBgColor?: string }) => {
  const [hoverTimeout, setHoverTimeout] = createSignal<number>();
  const [showPanel, setShowPanel] = createSignal(false);
  const [panelAnchor, setPanelAnchor] = createSignal({ left: 0, bottom: 0 });
  let triggerRef: HTMLDivElement | undefined;
  const { getWebsiteUrl, currentCompany } = useCurrentCompany();
  const { isMobileView } = useDeviceDetect();
  const [showQuickLinks, setShowQuickLinks] = createSignal(false);
  const [showFeedback, setShowFeedback] = createSignal(false);
  const { t } = useLocalization();
  const menu = useMenu();
  const { setIsSidebarOpen } = menu!;

  const quickLinks = createMemo(() => {
    const websiteUrl = getWebsiteUrl('/');
    return [
      { name: t('Company website'), href: websiteUrl },
      { name: t('Tenant portal'), href: websiteUrl + 'tenants' },
      { name: t('Owner portal'), href: websiteUrl + 'owners' },
    ];
  });

  const handleMouseEnter = () => {
    clearTimeout(hoverTimeout());
    const rect = triggerRef?.getBoundingClientRect();
    if (rect) {
      setPanelAnchor({ left: rect.right, bottom: window.innerHeight - rect.bottom });
    }
    setShowPanel(true);
  };

  const handleLinkClick = () => {
    if (isMobileView()) {
      setIsSidebarOpen(false);
      setShowQuickLinks(!showQuickLinks());
    }
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimeout());
    const timeout = setTimeout(() => setShowPanel(false), 300);
    setHoverTimeout(timeout as unknown as number);
  };

  return (
    <div
      class="flex flex-col justify-center gap-4 border-t border-white/40 pt-4"
      classList={{
        'mx-2 mb-4 items-center': props.isMenuCollapsed,
        'm-4 items-stretch': !props.isMenuCollapsed,
      }}>
      <Show
        when={!props.isMenuCollapsed}
        fallback={
          <div class="flex flex-col items-center gap-2">
            <Tooltip bgColor={props.tooltipBgColor} align="right" message={t('Quick links')}>
              <Button
                ghost
                color="white"
                variant="outlined"
                class="flex size-9 items-center justify-center border-white/40 p-0 text-white"
                onClick={() => setShowQuickLinks(true)}>
                <LinkIcon class="size-5" />
              </Button>
            </Tooltip>
            <Tooltip bgColor={props.tooltipBgColor} align="right" message={t('Feedback')}>
              <Button
                ghost
                color="white"
                variant="outlined"
                class="flex size-9 items-center justify-center border-white/40 p-0 text-white"
                onClick={() => setShowFeedback(true)}>
                <IconPencilLine class="size-5" />
              </Button>
            </Tooltip>
          </div>
        }>
        <div
          ref={triggerRef}
          class="group relative inline-block"
          onMouseEnter={() => !isMobileView() && handleMouseEnter()}
          onMouseLeave={() => !isMobileView() && handleMouseLeave()}>
          <Button variant="outlined" ghost class="flex w-full justify-center border-white/40 p-2 text-white" onClick={handleLinkClick}>
            <div class="flex w-40 items-center gap-x-2">
              <LinkIcon class="size-5" />
              {t('Quick links')}
            </div>
            <IconChevronRight class="size-5" />
          </Button>
          <Show when={!isMobileView() && showPanel()}>
            <Portal>
              <div
                class="fixed z-50 w-[28rem] rounded-lg pl-6 transition-opacity duration-200"
                style={{ left: `${panelAnchor().left}px`, bottom: `${panelAnchor().bottom}px` }}
                onMouseEnter={() => handleMouseEnter()}
                onMouseLeave={() => handleMouseLeave()}>
                <QuickLinksPanel quickLinks={quickLinks()} />
              </div>
            </Portal>
          </Show>
        </div>

        <Button
          variant="outlined"
          ghost
          class="flex w-full justify-between border-white/40 p-2 pl-4 text-white"
          onClick={() => setShowFeedback(true)}>
          <IconPencilLine class="size-5" />
          {t('Feedback')}
        </Button>

        <Show when={currentCompany()}>
          <div class="flex flex-col items-center justify-center gap-2 text-sm text-white">
            <p class="text-xs text-white/80">{t('Company phone number')}:</p>
            <p>{currentCompany()?.phoneNumber || t(`Not selected yet`)}</p>
          </div>
        </Show>
      </Show>
      <Modal visible={showQuickLinks()} showFooter={false}>
        <div class="relative">
          <CloseIcon class="absolute right-1 top-1 z-10 cursor-pointer" onClick={() => setShowQuickLinks(false)} />
          <QuickLinksPanel quickLinks={quickLinks()} />
        </div>
      </Modal>
      <FeedbackModal open={showFeedback()} onClose={() => setShowFeedback(false)} />
    </div>
  );
};
